import Button from '@ui/buttons/Button'
import { pdSchema, RefractionUtils, RefractionValidations } from '@ca/common'
import Segmented from '@ui/elements/Segmented'
import { SegmentedOption } from '@ui/elements/Segmented/types'
import FormInput from '@ui/forms/Input/FormInput'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  PdOption,
  PdOptions,
  PdOverrideValues,
} from '@order/hooks/state/orderState'
import { useOrderState } from '@order/hooks/useOrderState'
import { useLastOrderDefaults } from '@order/hooks/useSaveOrderDefaults'
import { trpc } from 'api'
import { useCustomer } from 'hooks/useSession'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import { z } from 'zod'
import { PdPrescriptionMode, usePDPrescription } from './usePdPrescription'
import { refineCommaInput } from '@ca/common'
import { ErpOrderState } from '@ca/common/types'
import { MeasurementLocationType } from 'hooks/measurements/useMeasureSdk'
import { useMeasure } from 'features/measurements/useMeasure'

const allOptions = Object.values(PdOptions)

const range = {
  min: RefractionValidations.pd.min,
  max: RefractionValidations.pd.max,
}
const rangeString = `${range.min.toFixed(2)}  —  ${range.max.toFixed(2)} mm`

export type PdFormProps = {
  allowed?: PdOption[]
  children?: React.ReactNode
  prescription?: PdPrescriptionMode
  location?: MeasurementLocationType
  disabled?: boolean
}

export function PdForm(props: PdFormProps) {
  const { children, location, disabled } = props
  const { method, onMeasure: onMeasurePD } = useMeasure('pd', { location })
  const { t } = useTranslation(['measurement', 'validation'])
  const text = 'measurement:pd'
  const isPdMeasurementEnabled = method !== 'disabled'

  const prescriptionMode = props.prescription ?? 'active'
  const allowed = props?.allowed ?? allOptions
  const pdCurrent = useOrderState((s) => s.order.pd.current)
  const measurementPd = useOrderState((s) => s.order.pd.values.measurement)
  const manualPd = useOrderState((s) => s.order.pd.values.manual)
  const prescription = usePDPrescription(prescriptionMode)

  const isPrescriptionPdValid = useMemo(() => {
    if (!prescription) return false
    return pdSchema.safeParse({
      r_pd: prescription.r_pd,
      l_pd: prescription.l_pd,
    }).success
  }, [prescription])

  const minmaxMessage = t('validation:pd.minmax', range)

  const pdMeasurementSchema = useMemo(
    () =>
      z.object({
        r_pd: z.coerce
          .number()
          .min(range.min, minmaxMessage)
          .max(range.max, minmaxMessage),
        l_pd: z.coerce
          .number()
          .min(range.min, minmaxMessage)
          .max(range.max, minmaxMessage),
      }),
    [minmaxMessage]
  )

  const updatePD = useOrderState((s) => s.updatePd)

  const { lastUsedPd, lastUsedPdOption } = useLastOrderDefaults()

  const customer = useCustomer()
  const { data: latestOrder } = trpc.order.latest.useQuery(
    {
      customer_id: customer?.id ?? '',
      states: [ErpOrderState.sale],
    },
    {
      enabled: !!customer?.id && allowed.includes(PdOptions.latestOrder),
    }
  )

  const lastOrderRefraction = latestOrder?.config?.refraction

  // Current default can change when we load data
  const defaults: Record<PdOption, PdOverrideValues> = useMemo(() => {
    return {
      [PdOptions.latestOrder]: {
        r_pd: lastOrderRefraction?.r_pd?.toFixed(2) ?? '',
        l_pd: lastOrderRefraction?.l_pd?.toFixed(2) ?? '',
      },
      [PdOptions.prescription]: {
        r_pd: prescription?.r_pd?.toFixed(2) ?? '',
        l_pd: prescription?.l_pd?.toFixed(2) ?? '',
      },
      [PdOptions.measurement]: {
        r_pd: measurementPd?.r_pd ?? '',
        l_pd: measurementPd?.l_pd ?? '',
      },
      [PdOptions.manual]: {
        r_pd: manualPd?.r_pd ?? '',
        l_pd: manualPd?.l_pd ?? '',
      },
    }
  }, [lastOrderRefraction, prescription, measurementPd, manualPd])

  const options: SegmentedOption<PdOption>[] = useMemo(
    () =>
      [
        {
          id: PdOptions.prescription,
          label:
            prescriptionMode === 'active'
              ? t(`${text}.label.prescription`)
              : t(`${text}.label.prescriptionLatest`),
          disabled: !isPrescriptionPdValid,
        },
        {
          id: PdOptions.latestOrder,
          label: t(`${text}.label.latestOrder`),
          disabled: !lastOrderRefraction,
        },
        {
          id: PdOptions.measurement,
          label: t(`${text}.label.measurement`),
          disabled: !isPdMeasurementEnabled,
          hidden: !isPdMeasurementEnabled,
        },
        { id: PdOptions.manual, label: t(`${text}.label.manual`) },
      ].filter((option) => allowed.includes(option.id) && !option.hidden),
    [
      allowed,
      lastOrderRefraction,
      t,
      isPrescriptionPdValid,
      prescriptionMode,
      isPdMeasurementEnabled,
    ]
  )

  const prescriptionId = prescription?.id ?? null

  const defaultOption = useMemo(() => {
    const optionMap = new Map(options.map((o, i) => [o.id, { ...o, index: i }]))
    const measurement = optionMap.get(PdOptions.measurement)
    const manual = optionMap.get(PdOptions.manual)

    const isValidMeasurement = pdSchema.safeParse(measurementPd).success
    const isValidManual = pdSchema.safeParse(manualPd).success

    // Respect current tab if not disabled so manual user tab switches persist.
    const current = pdCurrent ? optionMap.get(pdCurrent) : undefined
    if (current && !current.disabled) {
      return current.index
    }

    // Prefer valid user-entered data when no current or current tab is gone/empty
    if (measurement && !measurement.disabled && isValidMeasurement)
      return measurement.index
    if (manual && isValidManual) return manual.index

    // Fall back to last used or first available
    if (lastUsedPdOption && lastUsedPd) {
      const saved = optionMap.get(lastUsedPdOption)
      if (saved && !saved.disabled) return saved.index
    }

    return options.findIndex((o) => !o.disabled)
  }, [
    pdCurrent,
    options,
    lastUsedPd,
    lastUsedPdOption,
    measurementPd,
    manualPd,
  ])

  const selectedOption = useMemo(() => {
    return options[defaultOption]
  }, [defaultOption, options])

  const hasCurrentPd = !!pdCurrent
  const currentDefault = useMemo(() => {
    if (!hasCurrentPd && lastUsedPdOption === selectedOption.id && lastUsedPd) {
      return {
        r_pd: lastUsedPd.r_pd.toFixed(2),
        l_pd: lastUsedPd.l_pd.toFixed(2),
      }
    }
    return defaults[selectedOption.id]
  }, [defaults, selectedOption.id, hasCurrentPd, lastUsedPdOption, lastUsedPd])

  const form = useForm<
    PdOverrideValues,
    unknown,
    z.infer<typeof pdMeasurementSchema>
  >({
    defaultValues: currentDefault,
    values: currentDefault,
    // react-hook-form ≥7.55: zodResolver infers z.input<schema> (uses z.coerce, so input is
    // `unknown`) as TFieldValues, mismatching PdOverrideValues. `as any` preserves pre-7.55
    // behavior — Zod still validates and transforms at runtime. See docs/expo54-migration.md § Known Issues.
    resolver: zodResolver(pdMeasurementSchema) as any,
    shouldFocusError: false,
    mode: 'onBlur',
  })

  const watchValues = useWatch({ control: form.control })

  const prevTabRef = useRef(selectedOption.id)
  const prevPrescriptionIdRef = useRef(prescriptionId)

  useEffect(() => {
    const prescriptionChanged = prevPrescriptionIdRef.current !== prescriptionId
    prevPrescriptionIdRef.current = prescriptionId

    if (!prescriptionChanged || !prescriptionId || !isPrescriptionPdValid) {
      return
    }

    if (pdCurrent !== PdOptions.measurement && pdCurrent !== PdOptions.manual) {
      return
    }

    const isCurrentTabDataValid =
      pdCurrent === PdOptions.measurement
        ? pdSchema.safeParse(measurementPd).success
        : pdSchema.safeParse(manualPd).success

    if (isCurrentTabDataValid) {
      return
    }

    updatePD(defaults[PdOptions.prescription], PdOptions.prescription)
  }, [
    prescriptionId,
    pdCurrent,
    isPrescriptionPdValid,
    measurementPd,
    manualPd,
    defaults,
    updatePD,
  ])

  useEffect(() => {
    if (disabled) return

    const tabChanged = prevTabRef.current !== selectedOption.id
    prevTabRef.current = selectedOption.id

    if (tabChanged || selectedOption.id !== PdOptions.manual) {
      // Tab switched (use authoritative defaults, not stale watchValues),
      // or non-manual tab where external data changed (prescription, latestOrder, measurement).
      // Guard the reset to avoid re-triggering watchValues unnecessarily.
      const curr = form.getValues()
      if (!RefractionUtils.pd.isSame(curr, currentDefault)) {
        form.reset(currentDefault)
      }
      updatePD(currentDefault, selectedOption.id)
      return
    }

    // Manual tab, no tab change: sync what the user typed
    updatePD(watchValues, PdOptions.manual)
  }, [disabled, watchValues, selectedOption.id, currentDefault, form, updatePD])

  const onSegmentedChange = useCallback(
    (option: SegmentedOption<PdOption>) => {
      // Switching to manual should allow to edit the data from previous option

      if (disabled) return

      if (option.id === PdOptions.manual) {
        const formValues = form.getValues()
        updatePD(formValues, option.id)
        return
      }
      const currentDefault = defaults[option.id]

      updatePD(currentDefault, option.id)
      form.reset(currentDefault)
    },
    [defaults, form, updatePD, disabled]
  )

  const inputsDisabled = selectedOption.id !== PdOptions.manual

  return (
    <View className="gap-8">
      <Segmented
        options={options}
        value={selectedOption}
        onChange={onSegmentedChange}
      />
      <View className="gap-6">
        <FormInput
          control={form.control}
          name="r_pd"
          label={t(`${text}.right`)}
          placeholder="—"
          textEnd={rangeString}
          keyboardType="decimal-pad"
          refine={refineCommaInput}
          inputMode="decimal"
          disabled={inputsDisabled}
        />
        <FormInput
          control={form.control}
          name="l_pd"
          label={t(`${text}.left`)}
          placeholder="—"
          textEnd={rangeString}
          keyboardType="decimal-pad"
          inputMode="decimal"
          refine={refineCommaInput}
          disabled={inputsDisabled}
        />
      </View>
      {(children || selectedOption.id === PdOptions.measurement) && (
        <View className="flex flex-row justify-start gap-4">
          {children}
          {selectedOption.id === PdOptions.measurement && (
            <Button
              label={t(`${text}.measure`)}
              onPress={() => onMeasurePD({ manual: false })}
              variant="outlined"
            />
          )}
        </View>
      )}
    </View>
  )
}
